import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, Loader2 } from "lucide-react";

import {
  fetchMyNotificationPreferences,
  saveMyNotificationPreferences,
  type NotificationPreferences,
} from "@/lib/api/notification-prefs";

import { Card } from "@/components/ui/Card";

interface OptOutOption {
  key: string;
  label: string;
  hint: string;
}

// Surfaced toggles. Critical pushes (reveal, mutual matches, event
// countdowns) are intentionally not listed — opting out of those
// would silently break the product, and the rules engine bypasses
// opt-out for them anyway.
const OPT_OUT_OPTIONS: OptOutOption[] = [
  {
    key: "like_received",
    label: "Someone liked you",
    hint: "Single push when you're noticed; intrigue, no name.",
  },
  {
    key: "new_relevant_people",
    label: "New relevant people",
    hint: "When 3+ people fitting your goals join.",
  },
  {
    key: "pitch_views_summary",
    label: "Your pitch was watched",
    hint: "Daily summary if 3+ different people watched.",
  },
  {
    key: "finish_onboarding",
    label: "Finish your profile reminders",
    hint: "Nudges if you registered but haven't set up.",
  },
  {
    key: "data_expiry_reminder",
    label: "Data expiry reminder",
    hint: "3 days before contacts are auto-deleted.",
  },
];

/**
 * V2-13 — Notification preferences block, slotted into
 * ProfileSettingsPage right under the Telegram connect block.
 *
 *   * Digest toggle — collapses A1/A2/B1 into one daily push.
 *     Critical pushes stay instant (rules engine skips deferral).
 *   * Per-type opt-outs — the noisy types only; critical pushes
 *     aren't toggleable so the product can't be silently broken.
 *   * Timezone — auto-detected from the browser; we show it so the
 *     user knows what "quiet hours" mean for them.
 */
export function NotificationPreferencesBlock() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notification-prefs"],
    queryFn: fetchMyNotificationPreferences,
  });

  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data, draft]);

  const mutation = useMutation({
    mutationFn: saveMyNotificationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-prefs"] });
      setSavedAt(Date.now());
    },
  });

  if (isLoading || !draft) {
    return (
      <Card padding="md" className="mt-5">
        <div className="flex items-center gap-2 text-[13px] text-[#6c757d]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
        </div>
      </Card>
    );
  }

  const toggleType = (key: string) => {
    setDraft((d) => {
      if (!d) return d;
      const set = new Set(d.opted_out_types);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...d, opted_out_types: Array.from(set) };
    });
    setSavedAt(null);
  };

  const setDigest = (v: boolean) => {
    setDraft((d) => (d ? { ...d, digest_mode: v } : d));
    setSavedAt(null);
  };

  const dirty =
    draft.digest_mode !== data?.digest_mode ||
    draft.opted_out_types.join("|") !== (data?.opted_out_types ?? []).join("|");

  return (
    <Card padding="md" className="mt-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#eff3fe] text-[#2563eb]">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
            Telegram notifications
          </p>
          <p className="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
            Tune what we ping you about
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-[#6c757d]">
            Matches, reveal moments, and event countdowns always go through
            instantly. The rest you can mute or collapse into a daily summary.
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-[10px] border border-[#e2e8f0] p-3">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={draft.digest_mode}
            onChange={(e) => setDigest(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#2563eb]"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-semibold text-[#1a1a1a]">
              Daily digest mode
            </span>
            <span className="mt-0.5 block text-[12px] leading-relaxed text-[#6c757d]">
              New people, pitch views and like notifications collapse into one
              push at the end of your day.
            </span>
          </span>
        </label>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {OPT_OUT_OPTIONS.map((opt) => {
          const muted = draft.opted_out_types.includes(opt.key);
          return (
            <label
              key={opt.key}
              className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-[#e2e8f0] p-3"
            >
              <input
                type="checkbox"
                checked={!muted}
                onChange={() => toggleType(opt.key)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#2563eb]"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-semibold text-[#1a1a1a]">
                  {opt.label}
                </span>
                <span className="mt-0.5 block text-[12px] leading-relaxed text-[#6c757d]">
                  {opt.hint}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-[#94a3b8]">
        Quiet hours: 22:00–08:00 in your local time ({draft.timezone}). No pings
        in that window — accumulated ones go out in the morning.
      </p>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate(draft)}
          className="inline-flex h-9 items-center justify-center rounded-[10px] bg-[#2563eb] px-4 text-[13px] font-semibold text-white disabled:opacity-50"
        >
          {mutation.isPending ? "Saving…" : "Save preferences"}
        </button>
        {savedAt && Date.now() - savedAt < 4000 ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#15803d]">
            <Check className="h-3.5 w-3.5" /> Saved
          </span>
        ) : null}
      </div>
    </Card>
  );
}
